// app/(app)/reports/returns.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import {
  reportsApi,
  ReturnReportItem,
  ReturnReportSummary,
} from "@/services/reportsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { showAlert } from "@/utils/alert";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system/legacy";
import { useFocusEffect, useRouter } from "expo-router";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useState } from "react";
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function ReturnReportScreen() {
  const { colors, spacing } = useAppTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { customers, loading: customersLoading } = useAppSelector(
    (state) => state.customers,
  );

  // Filter states
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [status, setStatus] = useState<string>("");

  // Data states
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState<ReturnReportSummary | null>(null);
  const [returns, setReturns] = useState<ReturnReportItem[]>([]);

  // Areas state
  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Search states
  const [customerSearch, setCustomerSearch] = useState("");

  // Load areas on mount
  useFocusEffect(
    useCallback(() => {
      loadAreas();
    }, []),
  );

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Debounced customer search (with optional area filter)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (customerSearch !== undefined) {
        if (selectedAreaId !== "") {
          dispatch(
            fetchCustomers({
              page: 1,
              limit: 20,
              search: customerSearch,
              areaId: Number(selectedAreaId),
            }),
          );
        } else {
          dispatch(
            fetchCustomers({ page: 1, limit: 20, search: customerSearch }),
          );
        }
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (customerName.trim()) params.customerName = customerName;
      if (status) params.status = status;
      const response = await reportsApi.getReturnReport(params);
      setSummary(response.summary);
      setReturns(response.data);
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (customerName.trim()) params.customerName = customerName;
      if (status) params.status = status;
      const blob = await reportsApi.downloadReturnReportPdf(params);

      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `return_report_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const handleCustomerSelect = (value: string, label: string) => {
    setSelectedCustomerId(value);
    setCustomerName(label);
    setCustomerSearch("");
  };

  const handleClearCustomer = () => {
    setSelectedCustomerId("");
    setCustomerName("");
    setCustomerSearch("");
  };

  const renderReturnItem = ({ item }: { item: ReturnReportItem }) => {
    const statusColor =
      item.status === "approved"
        ? colors.success
        : item.status === "pending"
          ? colors.disabled
          : colors.danger;
    return (
      <View style={styles.returnCard}>
        <View style={styles.returnHeader}>
          <Text style={styles.invoiceNo}>{item.invoiceNo}</Text>
          <Text style={[styles.statusText, { color: statusColor }]}>
            {item.status.toUpperCase()}
          </Text>
        </View>
        <Text style={styles.customerName}>{item.customerName}</Text>
        <Text style={styles.productName}>{item.productName}</Text>
        <View style={styles.returnDetails}>
          <Text style={styles.detailText}>Qty: {item.quantity}</Text>
          <Text style={styles.detailText}>
            Amount: ₹{parseFloat(item.amount).toFixed(2)}
          </Text>
        </View>
        <Text style={styles.returnDate}>
          {new Date(item.returnDate).toLocaleDateString()}
        </Text>
        {item.reason && (
          <Text style={styles.reasonText}>Reason: {item.reason}</Text>
        )}
      </View>
    );
  };

  // Compute customer options with selected customer always included
  const getCustomerOptions = () => {
    const baseOptions = customers.map((c) => ({
      label: `${c.fullName} (${c.phone})${c.Area?.name ? ` - ${c.Area.name}` : ""}`,
      value: c.id.toString(),
    }));
    if (
      selectedCustomerId &&
      !baseOptions.some((opt) => opt.value === selectedCustomerId)
    ) {
      const selectedCust = customers.find(
        (c) => c.id.toString() === selectedCustomerId,
      );
      if (selectedCust) {
        baseOptions.unshift({
          label: `${selectedCust.fullName} (${selectedCust.phone})${selectedCust.Area?.name ? ` - ${selectedCust.Area.name}` : ""}`,
          value: selectedCust.id.toString(),
        });
      } else if (customerName) {
        baseOptions.unshift({
          label: customerName,
          value: selectedCustomerId,
        });
      }
    }
    return baseOptions;
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    filterRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.sm,
      marginBottom: spacing.md,
      alignItems: "center",
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    inputContainer: { marginBottom: spacing.md },
    buttonRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.lg,
    },
    summaryCard: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
    },
    summaryTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    summaryLabel: { fontSize: 14, color: colors.textLight },
    summaryValue: { fontSize: 14, fontWeight: "500", color: colors.text },
    grandValue: { fontSize: 16, fontWeight: "700", color: colors.primary },
    returnCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.md,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    returnHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    invoiceNo: { fontSize: 14, fontWeight: "600", color: colors.text },
    statusText: { fontSize: 12, fontWeight: "500" },
    customerName: { fontSize: 14, color: colors.text, marginBottom: 2 },
    productName: { fontSize: 14, color: colors.text, marginBottom: 2 },
    returnDetails: {
      flexDirection: "row",
      gap: spacing.md,
      marginBottom: 2,
    },
    detailText: { fontSize: 12, color: colors.textLight },
    returnDate: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    reasonText: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
          >
            <Ionicons name="arrow-back" size={24} color={colors.primary} />
            <Text style={{ color: colors.primary, marginLeft: 4 }}>Back</Text>
          </TouchableOpacity>

          <Text style={styles.title}>Return Report</Text>

          <View style={styles.filterRow}>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            {(startDate || endDate) && (
              <TouchableOpacity
                style={[styles.filterButton, { borderColor: colors.danger }]}
                onPress={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                <Text style={{ color: colors.danger }}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          <SearchableSelect
            label="Area / Route"
            placeholder="All areas"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={setSelectedAreaId}
            loading={loadingAreas}
            containerStyle={styles.inputContainer}
            clearable
          />

          <SearchableSelect
            label="Customer"
            placeholder="All customers"
            options={getCustomerOptions()}
            value={selectedCustomerId}
            onValueChange={handleCustomerSelect}
            onSearch={setCustomerSearch}
            loading={customersLoading}
            containerStyle={styles.inputContainer}
            clearable
          />

          <SearchableSelect
            label="Status"
            placeholder="All statuses"
            options={[
              { label: "Pending", value: "pending" },
              { label: "Approved", value: "approved" },
              { label: "Rejected", value: "rejected" },
            ]}
            value={status}
            onValueChange={setStatus}
            containerStyle={styles.inputContainer}
            clearable
          />

          <View style={styles.buttonRow}>
            <Button
              title="Apply Filters"
              onPress={fetchReport}
              loading={loading}
              variant="primary"
            />
            <Button
              title="Download PDF"
              onPress={handleDownloadPdf}
              loading={downloading}
              variant="outline"
            />
          </View>

          {summary && (
            <View style={styles.summaryCard}>
              <Text style={styles.summaryTitle}>Summary</Text>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Returns:</Text>
                <Text style={styles.summaryValue}>{summary.totalReturns}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Quantity:</Text>
                <Text style={styles.summaryValue}>{summary.totalQuantity}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Amount:</Text>
                <Text style={[styles.summaryValue, styles.grandValue]}>
                  ₹{parseFloat(summary.totalAmount).toFixed(2)}
                </Text>
              </View>
            </View>
          )}

          <FlatList
            data={returns}
            keyExtractor={(item, index) => `${item.slNo}-${index}`}
            renderItem={renderReturnItem}
            scrollEnabled={false}
            ListEmptyComponent={
              !loading && returns.length === 0 ? (
                <Text style={styles.emptyText}>
                  No returns found. Apply filters and click "Apply Filters".
                </Text>
              ) : null
            }
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
